<!--
StaffSweepRunsModal — the sweep's RUN LOG (S12/#17a).

Opened from /config/assinaturas via `modal.staffSweepRuns.open`. Read-only:
every row describes a round that ALREADY happened, whichever door fired it —
the scheduler (cli), the roster's "Processar vencidas" button (staff_page), or
one subscription's "Processar agora" (staff_subscription).

The log is READ ON OPEN, never cached. A round the staff just triggered has to
appear the next time they open this modal, and a module-scope copy would show
them the log as it was before their own click.

Clicking a round's date opens its OUTCOMES: what the round did to each
candidate. That trail exists nowhere else — a cut also lands in
subscription_events, but a deferred, a refused, a gateway failure, a not_due or
a breaker refusal is recorded only here.

Empty fields render "—". NULL means unknown, never zero and never a guess.
-->
<template>
  <modal
    elem-id="modal_staff_sweep_runs"
    :title="t('Histórico de rodadas')"
    :show="isVisible"
    size="modalMd"
    :close-label="t('Fechar')"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div data-testid="staff-sweep-runs-modal">
        <!--
          A refusal arrives on HTTP 200 with `success:false`, so the composable
          throws and we land here. Showing the (empty) table anyway would tell
          the staff "nenhuma rodada" for a log they were never allowed to read —
          the opposite of the truth.
        -->
        <inline-message
          v-if="loadError"
          severity="error"
          data-testid="staff-sweep-runs-error"
        >
          {{ t('Não foi possível carregar o histórico de rodadas') }}: {{ loadError }}
        </inline-message>

        <data-table
          v-else
          test-id-prefix="staff-sweep-runs"
          :config="tableConfig"
          :data="runRows"
          display-mode="table"
        />

        <box
          v-if="selectedRun"
          type="info"
          icon="pi pi-list"
          :title="`${t('O que a rodada fez')} — ${selectedRunWhen}`"
          elem-id="boxStaffSweepRunOutcomes"
        >
          <div data-testid="staff-sweep-runs-outcomes">
            <data-table
              test-id-prefix="staff-sweep-run-outcomes"
              :config="outcomesConfig"
              :data="outcomeRows"
              display-mode="table"
            />
          </div>
        </box>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useSubscriptionAdmin } from '../../../composables/useSubscriptionAdmin'
import { useDateFormatter } from '../../../composables/useDateFormatter'
import Modal from '../../../ui/Modal.vue'
import Box from '../../../ui/Box.vue'
import InlineMessage from '../../../ui/InlineMessage.vue'
import DataTable from '../../../grid/DataTable.vue'
import type { Subscriptions_AdminSweepRunRow } from '../../../contracts/Subscriptions_AdminSweepRunRow'

const { t } = useI18n()
const { fetchSweepRuns } = useSubscriptionAdmin()
const { formatDateTime, fromApiDateTimeFormat } = useDateFormatter()

const EMPTY = '—'

const isVisible = ref(false)
const loadError = ref<string | null>(null)
const runs = ref<Subscriptions_AdminSweepRunRow[]>([])
const selectedRunId = ref<number | null>(null)

/**
 * The doors the sweep can be fired through. 'cli' (the crontab curl) and 'cron'
 * (the S10 scheduled job, #17b) are deliberately separate labels: during the
 * crontab→agendador transition both exist, and "which one actually ran last
 * night" is the question this column has to answer.
 */
function triggerLabel(source: string | undefined): string {
  const labels: Record<string, string> = {
    cli: t('CLI (crontab)'),
    cron: t('Agendador'),
    staff_page: t('Manual — página'),
    staff_subscription: t('Manual — assinatura'),
  }
  if (!source) return EMPTY
  return labels[source] ?? source
}

/** The unattended doors have no user behind them — a shared secret, or the tick itself. */
function callerLabel(caller: string | undefined): string {
  if (!caller) return EMPTY
  if (caller === 'internal-token') return t('interno')
  if (caller === 'scheduler') return t('agendador')
  return caller
}

/**
 * The outcome vocabulary the sweep writes: the cancellation service's own codes
 * plus the three the sweep itself produces.
 */
function outcomeLabel(outcome: string | undefined): string {
  const labels: Record<string, string> = {
    cancelled: t('Cortada'),
    deferred: t('Adiada'),
    refused: t('Recusada'),
    gateway_failed: t('Falha no gateway'),
    local_write_failed: t('Falha ao gravar'),
    not_due: t('Ainda em carência'),
    skipped: t('Ignorada'),
    not_found: t('Não encontrada'),
  }
  if (!outcome) return EMPTY
  return labels[outcome] ?? outcome
}

function whenDisplay(row: Subscriptions_AdminSweepRunRow): string {
  if (!row.started_at) return EMPTY
  return formatDateTime(fromApiDateTimeFormat(row.started_at))
}

function countDisplay(value: number | undefined): string {
  return value == null ? EMPTY : String(value)
}

const runRows = computed(() =>
  runs.value.map((r) => ({
    id: r.id,
    when_display: whenDisplay(r),
    trigger_display: triggerLabel(r.trigger_source),
    caller_display: callerLabel(r.triggered_by),
    target_display: r.target_subscription_id == null ? EMPTY : String(r.target_subscription_id),
    candidate_display: countDisplay(r.candidate_count),
    cancelled_display: countDisplay(r.cancelled_count),
    deferred_display: countDisplay(r.deferred_count),
    refused_display: countDisplay(r.refused_count),
    failed_display: countDisplay(r.failed_count),
    // The breaker standing down is the one thing in this table that is not a
    // count — the round was refused before it touched anything.
    circuit_display: r.circuit_open === true ? t('Rodada recusada') : EMPTY,
  })),
)

const tableConfig = computed(() => ({
  tableId: 'tabelaStaffSweepRuns',
  title: t('Rodadas'),
  columns: [
    {
      // The date is the door to the round's outcomes: the per-candidate trail
      // is the only place a deferred or a refused is recorded.
      data: 'when_display',
      title: t('Quando'),
      renderType: 'custom_clickable_id',
      clickCallback: (_value: unknown, row?: Record<string, unknown>) => {
        if (row) selectedRunId.value = row.id as number
      },
    },
    { data: 'trigger_display', title: t('Origem') },
    { data: 'caller_display', title: t('Quem') },
    { data: 'target_display', title: t('Alvo') },
    { data: 'candidate_display', title: t('Candidatas') },
    { data: 'cancelled_display', title: t('Cortadas') },
    { data: 'deferred_display', title: t('Adiadas') },
    { data: 'refused_display', title: t('Recusadas') },
    { data: 'failed_display', title: t('Falhas') },
    { data: 'circuit_display', title: t('Circuit breaker') },
  ],
  pagination: true,
  pageLength: 25,
  rowTestId: (row: Record<string, unknown>) =>
    row?.id == null ? '' : `staff-sweep-runs-row-${row.id as number}`,
}))

const selectedRun = computed((): Subscriptions_AdminSweepRunRow | null =>
  runs.value.find((r) => r.id === selectedRunId.value) ?? null,
)

const selectedRunWhen = computed((): string =>
  selectedRun.value ? whenDisplay(selectedRun.value) : EMPTY,
)

const outcomeRows = computed(() =>
  (selectedRun.value?.outcomes ?? []).map((o) => ({
    subscription_display: o.subscription_id == null ? EMPTY : String(o.subscription_id),
    outcome_display: outcomeLabel(o.outcome),
    // The machine code travels VERBATIM: it is what the backend recorded, and
    // translating it would hide the string the staff has to quote back.
    reason_display: o.reason ?? EMPTY,
  })),
)

const outcomesConfig = computed(() => ({
  tableId: 'tabelaStaffSweepRunOutcomes',
  columns: [
    { data: 'subscription_display', title: t('Assinatura') },
    { data: 'outcome_display', title: t('Resultado') },
    { data: 'reason_display', title: t('Motivo') },
  ],
  pagination: true,
  pageLength: 25,
}))

async function loadRuns(): Promise<void> {
  loadError.value = null
  try {
    runs.value = await fetchSweepRuns()
  } catch (err) {
    const reason = (err as Error)?.message ?? ''
    log('StaffSweepRunsModal', 'loadRuns', 'Erro ao carregar histórico de rodadas', { reason })
    runs.value = []
    loadError.value = reason
    eventBus.emit('globalMessage.error', `${t('Não foi possível carregar o histórico de rodadas')}: ${reason}`)
  }
}

/** Read on OPEN — the round the staff just ran must be in the next answer. */
function openModal(): void {
  log('StaffSweepRunsModal', 'openModal', 'Abrindo histórico de rodadas')
  isVisible.value = true
  runs.value = []
  selectedRunId.value = null
  void loadRuns()
}

function closeModal(): void {
  log('StaffSweepRunsModal', 'closeModal', 'Fechando histórico de rodadas')
  isVisible.value = false
  runs.value = []
  selectedRunId.value = null
  loadError.value = null
}

onMounted(() => {
  eventBus.on('modal.staffSweepRuns.open', openModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.staffSweepRuns.open', openModal)
})
</script>
